import { Component, ElementRef, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';

/** Longest comment the email may carry (words). */
export const COMMENT_MAX_WORDS = 100;

const countWords = (text: string): number => (text.trim() ? text.trim().split(/\s+/).length : 0);

export interface ViewDraftEmailData {
  to: string;
  cc: string;
  from?: string;
  subject: string;
  html: string;
  attachmentName?: string;
  reminderLevel?: number;
  errorCount?: number;
  vesselName?: string;
  canDraft?: boolean;
  /** Popup title; defaults to the correction mail's. */
  heading?: string;
  /** Badge next to the vessel name (e.g. the kind of mail), shown instead of the reminder badge. */
  badge?: string;
  /** Port call mails: the cargo operation the body is written for, with a toggle to switch it. */
  operation?: CargoOperation;
  /** Whether the vessel's reports state the operation; otherwise the popup asks to choose. */
  operationDetected?: boolean;
  /** Fresh subject and body for another operation (required with `operation`). */
  loadOperation?: (operation: CargoOperation) => Promise<{ subject: string; html: string }>;
  /** Keep an empty CC empty instead of filling in operations@ (port call mails come from it). */
  noDefaultCc?: boolean;
  /** One-line note under the header, e.g. mails already logged for this report. */
  notice?: string;
}

export type CargoOperation = 'discharging' | 'loading';

@Component({
  selector: 'app-view-draft-email-popup',
  templateUrl: './view-draft-email-popup.component.html',
  styleUrls: ['./view-draft-email-popup.component.scss'],
})
export class ViewDraftEmailPopupComponent implements OnInit {
  @ViewChild('previewIframe') previewIframe!: ElementRef<HTMLIFrameElement>;

  emailTo: string = '';
  emailCc: string = '';
  emailSubject: string = '';
  emailHtml: string = '';

  safeIframeContent!: SafeHtml;

  readonly operations: CargoOperation[] = ['discharging', 'loading'];
  operation?: CargoOperation;
  switchingOperation = false;

  /** True once the body has been changed from the generated draft. */
  bodyEdited = false;

  /** Optional note from the team, shown in the email only when filled in. */
  comment = '';
  readonly commentMaxWords = COMMENT_MAX_WORDS;

  /** Body as generated, for "Reset body", and its serialized form for the "Edited" check. */
  private originalHtml = '';
  private originalSerialized: string | null = null;
  private resets = 0;
  /** The template's doctype; outerHTML drops it and email clients render differently without it. */
  private doctype = '<!DOCTYPE html>';
  /** Marks nodes the editor adds to the iframe so they never reach the email. */
  private static readonly EDITOR_ATTR = 'data-draft-editor';
  /** Marks the comment block, which is part of the email but not editable in place. */
  private static readonly COMMENT_ATTR = 'data-draft-comment';

  constructor(
    public dialogRef: MatDialogRef<ViewDraftEmailPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ViewDraftEmailData,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.emailTo = this.data?.to || '';
    this.emailCc = this.data?.cc || (this.data?.noDefaultCc ? '' : 'operations@merakishippingservices.com');
    this.emailSubject = this.data?.subject || '';
    this.emailHtml = this.data?.html || '';
    this.operation = this.data?.operation;
    this.originalHtml = this.emailHtml;
    this.doctype = this.emailHtml.match(/^\s*(<!doctype[^>]*>)/i)?.[1] ?? this.doctype;

    this.refreshSafeHtml();
  }

  /** Rewrite subject and body for the chosen cargo operation; body edits are replaced. */
  async setOperation(op: CargoOperation): Promise<void> {
    if (op === this.operation || this.switchingOperation || !this.data?.loadOperation) return;
    this.switchingOperation = true;
    try {
      const fresh = await this.data.loadOperation(op);
      if (this.bodyEdited) this.toastr.info('The email body was rewritten for the new cargo operation.', 'Body Replaced');
      this.operation = op;
      this.emailSubject = fresh.subject;
      this.emailHtml = fresh.html;
      this.originalHtml = fresh.html;
      this.doctype = fresh.html.match(/^\s*(<!doctype[^>]*>)/i)?.[1] ?? this.doctype;
      this.bodyEdited = false;
      this.originalSerialized = null;
      this.refreshSafeHtml();
    } catch {
      this.toastr.error('Could not prepare the email for that cargo operation. Please try again.');
    } finally {
      this.switchingOperation = false;
    }
  }

  private refreshSafeHtml(): void {
    this.safeIframeContent = this.sanitizer.bypassSecurityTrustHtml(this.emailHtml);
  }

  onIframeLoad(event: any): void {
    const iframe = event.target as HTMLIFrameElement;
    if (!iframe) return;
    this.initIframeEditable(iframe);
  }

  private iframeDoc(): Document | null {
    const iframe = this.previewIframe?.nativeElement;
    try {
      return iframe?.contentDocument || iframe?.contentWindow?.document || null;
    } catch {
      return null;
    }
  }

  /** Make the email body editable in place. Runs once per loaded document. */
  private initIframeEditable(iframe: HTMLIFrameElement): void {
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc?.body || doc.body.isContentEditable) return;
      doc.body.contentEditable = 'true';
      doc.body.spellcheck = true;

      const style = doc.createElement('style');
      style.setAttribute(ViewDraftEmailPopupComponent.EDITOR_ATTR, '');
      style.textContent = `
        body[contenteditable="true"] { outline: none; cursor: text; }
        body[contenteditable="true"] *:focus { outline: none; }
      `;
      doc.head.appendChild(style);

      // Iframe events run outside Angular's zone; re-enter it so the "Edited" state shows.
      doc.addEventListener('input', () => this.ngZone.run(() => this.markEdited(doc)));

      // Paste as plain text so copied styles cannot break the email's layout.
      doc.addEventListener('paste', (event: ClipboardEvent) => {
        const text = event.clipboardData?.getData('text/plain');
        if (text == null) return;
        event.preventDefault();
        doc.execCommand('insertText', false, text);
      });

      this.renderComment(doc);

      // Links stay editable text; clicking one must not navigate the preview.
      doc.addEventListener('click', (event) => {
        if ((event.target as HTMLElement)?.closest?.('a')) event.preventDefault();
      });
    } catch (err) {
      console.warn('Iframe contentEditable init warning:', err);
    }
  }

  private markEdited(doc: Document): void {
    this.originalSerialized ??= this.serialize(null);
    // "Edited" is about the body itself; the comment block does not count.
    this.bodyEdited = this.serialize(doc, false) !== this.originalSerialized;
    // Untouched (or undone back to the original) and no comment: send the generated HTML exactly as before.
    this.emailHtml = this.bodyEdited || this.commentText ? this.serialize(doc) : this.originalHtml;
  }

  get commentWords(): number {
    return countWords(this.comment);
  }

  private get commentText(): string {
    return this.comment.trim();
  }

  /** Keep the comment within the word limit and show it in the email preview as it is typed. */
  onCommentChange(value: string): void {
    const words = value.split(/(\s+)/);
    // Pieces alternate word / whitespace; cut after the last allowed word.
    let seen = 0;
    let cut = words.length;
    for (let i = 0; i < words.length; i++) {
      if (words[i].trim() && ++seen > COMMENT_MAX_WORDS) {
        cut = i;
        break;
      }
    }
    this.comment = cut < words.length ? words.slice(0, cut).join('').trimEnd() : value;
    const doc = this.iframeDoc();
    if (doc?.body) {
      this.renderComment(doc);
      this.markEdited(doc);
    }
  }

  /**
   * Put the comment block after the greeting (the template marks it with data-slot="intro"), or
   * remove it when the comment is empty. It is real email content, so it is serialized with the body.
   */
  private renderComment(doc: Document): void {
    const attr = ViewDraftEmailPopupComponent.COMMENT_ATTR;
    doc.querySelectorAll(`[${attr}]`).forEach((n) => n.remove());
    const text = this.commentText;
    if (!text) return;

    const escape = (t: string) =>
      t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const html = `
      <td class="px" style="padding: 16px 40px 0;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
          <tr>
            <td style="padding: 12px 16px; background: #F3F6F1; border-left: 3px solid #2E5A44; border-radius: 4px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;">
              <div style="font-size: 11px; letter-spacing: 0.8px; text-transform: uppercase; color: #2E5A44; font-weight: 700; margin-bottom: 4px;">Note from our team</div>
              <div style="font-size: 14.5px; line-height: 23px; color: #1E293B;">${escape(text).replace(/\n/g, '<br>')}</div>
            </td>
          </tr>
        </table>
      </td>`;

    const anchor = doc.querySelector('tr[data-slot="intro"]') ?? doc.querySelector('p')?.closest('tr');
    if (anchor?.parentNode) {
      const row = doc.createElement('tr');
      row.setAttribute(attr, '');
      row.setAttribute('contenteditable', 'false');
      row.innerHTML = html;
      anchor.parentNode.insertBefore(row, anchor.nextSibling);
    } else {
      const block = doc.createElement('div');
      block.setAttribute(attr, '');
      block.setAttribute('contenteditable', 'false');
      block.innerHTML = `<table role="presentation" width="100%"><tr>${html}</tr></table>`;
      doc.body.insertBefore(block, doc.body.firstChild);
    }
  }

  /**
   * The email as it should be sent: the template's doctype, then the edited document without the
   * editor's own markup (contenteditable, spellcheck, its style tag). `null` gives the original.
   */
  private serialize(doc: Document | null, withComment = true): string {
    if (!doc) {
      const parsed = new DOMParser().parseFromString(this.originalHtml, 'text/html');
      return `${this.doctype}\n${parsed.documentElement.outerHTML}`;
    }
    const { EDITOR_ATTR, COMMENT_ATTR } = ViewDraftEmailPopupComponent;
    const root = doc.documentElement.cloneNode(true) as HTMLElement;
    root.querySelectorAll(`[${EDITOR_ATTR}]`).forEach((n) => n.remove());
    root.querySelectorAll(`[${COMMENT_ATTR}]`).forEach((n) => {
      if (!withComment) {
        n.remove();
        return;
      }
      n.removeAttribute(COMMENT_ATTR);
      n.removeAttribute('contenteditable');
    });
    const body = root.querySelector('body');
    body?.removeAttribute('contenteditable');
    body?.removeAttribute('spellcheck');
    return `${this.doctype}\n${root.outerHTML}`;
  }

  /** Toolbar: apply a formatting command to the selection in the email body. */
  format(command: 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'undo' | 'redo'): void {
    const doc = this.iframeDoc();
    if (!doc?.body) return;
    doc.body.focus();
    doc.execCommand(command, false);
    this.markEdited(doc);
  }

  /** Discard body edits: reload the generated draft into a fresh document (load re-inits editing). */
  resetBody(): void {
    // A trailing comment lands outside <html>, so it changes the srcdoc without reaching the email.
    this.safeIframeContent = this.sanitizer.bypassSecurityTrustHtml(`${this.originalHtml}<!-- reset ${++this.resets} -->`);
    this.emailHtml = this.originalHtml;
    this.bodyEdited = false;
    // The reloaded document gets the comment again in initIframeEditable; send it if there is one.
    this.originalSerialized = null;
  }

  private syncFromIframe(): void {
    const doc = this.iframeDoc();
    if (doc?.documentElement) this.markEdited(doc);
  }

  getCurrentHtml(): string {
    this.syncFromIframe();
    return this.emailHtml;
  }

  onSend(): void {
    if (!this.emailTo || !this.emailTo.trim()) {
      this.toastr.warning('Please enter at least one recipient (To) email address.', 'Validation Warning');
      return;
    }
    if (!this.emailSubject || !this.emailSubject.trim()) {
      this.toastr.warning('Please enter an email subject line.', 'Validation Warning');
      return;
    }

    const finalHtml = this.getCurrentHtml();

    this.dialogRef.close({
      action: 'send',
      to: this.emailTo.trim(),
      cc: (this.emailCc || '').trim(),
      subject: this.emailSubject.trim(),
      html: finalHtml,
      operation: this.operation,
      // Only a filled-in comment is passed on (the HTML above already carries its block).
      comment: this.commentText || undefined,
    });
  }

  onClose(): void {
    this.dialogRef.close(null);
  }
}
